import { appContext } from '../AppContext';
import { PrepareBlobSasResponse, SasResponse } from './FileUtility';
import { getCurrentUser, isSpecialAccessLogin } from './UserUtility';
import { Buffer } from 'buffer';
import Stream from 'stream';
import util from 'util';

// TODO: should this be a class?
function FileStream(file, opt?) {
  Stream.Readable.call(this, opt);

  // MDN: FileReader constructor doesn't accept any args
  // https://developer.mozilla.org/en-US/docs/Web/API/FileReader#constructor
  this.fileReader = new FileReader();
  this.file = file;
  this.size = file.size;
  this.chunkSize = 1024 * 1024 * 4; // 4MB
  this.offset = 0;

  // `__me` and `this` should be the parent context in arrow functions
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const _me = this;
  this.fileReader.onloadend = (event) => {
    const data = event.target.result;
    const buf = Buffer.from(data);
    _me.push(buf);
  };

  this._read = () => {
    if (this.offset > this.size) this.push(null);
    else {
      const end = this.offset + this.chunkSize;
      const slice = this.file.slice(this.offset, end);
      this.fileReader.readAsArrayBuffer(slice);
      this.offset = end;
    }
  };
}
util.inherits(FileStream, Stream.Readable);

export function getDownloadUrl(fileId, sas) {
  return `${sas.containerUrl}/${sas.fileStoragePrefix}/${fileId}${sas.signature}`;
}

const fileIsImage = (contentType) => {
  const imageTypes = ['image/gif', 'image/jpeg', 'image/png'];
  return imageTypes.includes(contentType);
};

const getFileMeta = (file) =>
  new Promise((callback) => {
    const contentType = file.type;
    const size = `${file.size}`;

    if (fileIsImage(contentType)) {
      const img = new Image();
      img.onload = function () {
        const height = (this as any).height; // FIX
        const width = (this as any).width; // FIX
        callback({ contentType, height, size, width });
      };
      img.src = URL.createObjectURL(file);
    } else {
      callback({ contentType, size });
    }
  });

export type BlobMetadata = {
  UserId: string;
  IsFileAttachment: string;
  SpecialAccess: string;
};

export function getBlobMetadata(isFileAttachment = false) {
  const user = getCurrentUser();
  const isSpecialAccess = isSpecialAccessLogin();
  const metadata: BlobMetadata = { UserId: user?.id, IsFileAttachment: isFileAttachment.toString(), SpecialAccess: 'false' };
  if (!metadata.UserId) {
    // Public Form
    metadata.UserId = appContext.store.getState().formeditor.form?.userId || '';
  }
  if (isSpecialAccess) {
    metadata.SpecialAccess = 'true';
  }
  return metadata;
}

export function executeDownloadFile(fileId, sas) {
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = getDownloadUrl(fileId, sas);
  tempLink.setAttribute('download', 'test.png');

  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }

  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
}

export const fileToModel = (file, id, formId) =>
  new Promise((callback) => {
    return getFileMeta(file).then((metadata) => {
      callback({
        description: file.fileDescription,
        extension: file.fileExtension,
        formId,
        id,
        metadata,
        name: file.fileName,
        originalFileId: '',
        tags: file.tags || [],
        type: 2
      });
    });
  });

export async function uploadFile(file: File | Blob, sas: SasResponse, fileId?: string) {
  const { BlobServiceClient } = await import('@azure/storage-blob');
  const signature = sas.signature.substring(1);
  const blobServiceClient = BlobServiceClient.fromConnectionString(`BlobEndpoint=${sas.accountUrl};SharedAccessSignature=${signature}`);
  const blobId = `${sas.fileStoragePrefix}/${fileId || sas.guids[0]}`;
  const containerClient = blobServiceClient.getContainerClient(sas.container);
  const blockBlobClient = containerClient.getBlockBlobClient(blobId);

  await blockBlobClient.uploadData(file, {
    blobHTTPHeaders: {
      blobContentType: file.type,
      blobContentDisposition: file instanceof File ? `attachment; filename=${encodeURIComponent(file.name)}` : null
    },
    metadata: getBlobMetadata()
  });

  return {
    id: fileId || sas.guids[0],
    url: `${sas.containerUrl}/${blobId}`
  };
}

// todo: deprecated moved to FileUtility.js
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = [].slice.call(new Uint8Array(buffer));

  // eslint-disable-next-line no-return-assign
  bytes.forEach((b) => (binary += String.fromCharCode(b)));

  return window.btoa(binary);
}

export async function renameFile(file: TypeMePlease, fileId: string, sas: SasResponse) {
  const { BlobServiceClient } = await import('@azure/storage-blob');
  const signature = sas.signature.substring(1);
  const blobServiceClient = BlobServiceClient.fromConnectionString(`BlobEndpoint=${sas.accountUrl};SharedAccessSignature=${signature}`);
  const containerClient = blobServiceClient.getContainerClient(sas.container);
  const blobId = `${sas.fileStoragePrefix}/${fileId}`;
  const blockBlobClient = containerClient.getBlockBlobClient(blobId);

  blockBlobClient.setHTTPHeaders({
    blobContentType: file.type,
    blobContentDisposition: `attachment; filename=${encodeURIComponent(file.name)}.${file.extension}`
  });

  return { id: fileId, ok: true };
}

// todo: deprecated moved to FileUtility.js
export async function uploadFromBase64(base64: string, blobId: string, sas: SasResponse | PrepareBlobSasResponse) {
  const signature = sas.signature.substring(1);

  const { BlobServiceClient } = await import('@azure/storage-blob');
  const blobServiceClient = BlobServiceClient.fromConnectionString(`BlobEndpoint=${sas.accountUrl};SharedAccessSignature=${signature}`);
  const containerClient = blobServiceClient.getContainerClient(sas.container);
  const blockBlobClient = containerClient.getBlockBlobClient(`${sas.fileStoragePrefix}/${blobId}`);

  // eslint-disable-next-line no-useless-escape
  const matches = base64.match(/^data:([A-Za-z-+\/]+);(filename=.*;)?base64,(.+)$/);
  const contentType = matches[1];
  // eslint-disable-next-line no-buffer-constructor
  const buffer = new Buffer(matches[3], 'base64');

  await blockBlobClient.uploadData(buffer, {
    blobHTTPHeaders: {
      blobContentType: contentType
    },
    metadata: getBlobMetadata()
  });

  return {
    id: blobId,
    url: `${sas.containerUrl}/${sas.fileStoragePrefix}/${blobId}`,
    ok: true
  };
}

export async function uploadFromUrl(url: string, sas: SasResponse) {
  const response = await fetch(url);
  const [data, contentType] = await Promise.all([response.arrayBuffer(), response.headers.get('content-type')]);

  const blobId = sas.guids[0];
  const imageStr = arrayBufferToBase64(data);
  const base64 = `data:${contentType};base64,${imageStr}`;
  const uploadResult = await uploadFromBase64(base64, blobId, sas);
  return {
    id: uploadResult.id
  };
}
